import React, { useState, useEffect, useRef } from 'react';
import { Container, Card, Alert, Nav } from 'react-bootstrap';
import './Documentation.css';

function Documentation() {
  const [activeSection, setActiveSection] = useState('signup');
  
  // Refs for each section
  const sectionRefs = useRef({});

  // Handle scroll to section
  const scrollToSection = (sectionId) => {
    if (sectionRefs.current[sectionId]) {
      sectionRefs.current[sectionId].scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    setActiveSection(sectionId);
  };

  // Observe which section is in view
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(sectionRefs.current).forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  const sections = [
    { id: 'signup', label: '1. Sign Up', icon: 'bi-pencil-square' },
    { id: 'login', label: '2. Log In', icon: 'bi-box-arrow-in-right' },
    { id: 'forgot-password', label: '3. Forgot Password', icon: 'bi-key-fill' },
    { id: 'register', label: '4. Register New Person', icon: 'bi-person-plus-fill' },
    { id: 'update', label: '5. Update Person Data', icon: 'bi-pencil-fill' },
    { id: 'analytics', label: '6. View Analytics', icon: 'bi-bar-chart-fill' },
    { id: 'support', label: 'Need Help?', icon: 'bi-telephone-fill' },
  ];

  return (
    <div className="documentation-container">
      <Container fluid>
        <div className="docs-layout">
          {/* Side Navigation */}
          <aside className="docs-sidebar">
            <div className="sidebar-sticky">
              <h5 className="sidebar-title"><i className="bi bi-book-fill me-2"></i>Contents</h5>
              <Nav className="flex-column docs-nav">
                {sections.map((section) => (
                  <Nav.Link
                    key={section.id}
                    className={`docs-nav-link ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => scrollToSection(section.id)}
                  >
                    {/* <span className="nav-icon">{section.icon}</span> */}
                    <span className="nav-label">{section.label}</span>
                  </Nav.Link>
                ))}
              </Nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="docs-content">
            <div className="docs-header">
              <h1><i className="bi bi-book-fill me-2"></i>User Documentation</h1>
              <p>Complete guide to using the DoSS PWD Database Management System</p>
            </div>

            <Alert variant="info" className="docs-alert">
              <Alert.Heading><i className="bi bi-pin-fill me-1"></i>Quick Start</Alert.Heading>
              <p className="mb-0">
                New to the system? Start with the <strong>Sign Up</strong> section below, then follow the steps in order.
              </p>
            </Alert>

            {/* Sign Up Section */}
            <Card 
              className="docs-section" 
              id="signup"
              ref={(el) => (sectionRefs.current['signup'] = el)}
            >
              <Card.Body>
                <h2 className="section-title">1. Sign Up</h2>
                
                <div className="step-group">
                  <div className="step-item">
                    <span className="step-number">1</span>
                    <div className="step-content">
                      <p>Go to <strong>dosspwd.com</strong></p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">2</span>
                    <div className="step-content">
                      <p>Click <strong>Login</strong> on the navigation bar</p>
                      <ul>
                        <li>On mobile phones: Click the hamburger menu icon (☰) and select Login</li>
                      </ul>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">3</span>
                    <div className="step-content">
                      <p>Find <strong>"Don't have an account? Sign Up"</strong> and click <strong>Sign Up</strong></p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">4</span>
                    <div className="step-content">
                      <p>Enter your email address</p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">5</span>
                    <div className="step-content">
                      <p>Enter a secure password</p>
                      <Alert variant="warning" className="password-requirements">
                        <strong>Password Requirements:</strong>
                        <ul className="mb-0 mt-2">
                          <li>At least 8 characters</li>
                          <li>At least 3 of the following:
                            <ul>
                              <li>Lower case letters (a-z)</li>
                              <li>Upper case letters (A-Z)</li>
                              <li>Numbers (0-9)</li>
                              <li>Special characters (e.g. !@#$%^&*)</li>
                            </ul>
                          </li>
                        </ul>
                      </Alert>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">6</span>
                    <div className="step-content">
                      <p>Click <strong>Continue</strong></p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">7</span>
                    <div className="step-content">
                      <p>You will be directed to the home screen</p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">8</span>
                    <div className="step-content">
                      <p>You will see a badge labeled <span className="badge bg-warning text-dark">Pending</span></p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">9</span>
                    <div className="step-content">
                      <p>Wait for approval from the Department of Social Services</p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">10</span>
                    <div className="step-content">
                      <p>Once approved, <strong>Log out</strong> by clicking the logout button on the navigation bar, then <strong>sign in again</strong> using your email and password</p>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Log In Section */}
            <Card 
              className="docs-section" 
              id="login"
              ref={(el) => (sectionRefs.current['login'] = el)}
            >
              <Card.Body>
                <h2 className="section-title">2. Log In</h2>
                
                <div className="step-group">
                  <div className="step-item">
                    <span className="step-number">1</span>
                    <div className="step-content">
                      <p>Go to <strong>dosspwd.com</strong></p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">2</span>
                    <div className="step-content">
                      <p>Click <strong>Login</strong> on the navigation bar</p>
                      <ul>
                        <li>On mobile phones: Click the hamburger menu icon (☰) and select Login</li>
                      </ul>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">3</span>
                    <div className="step-content">
                      <p>Enter your registered email address</p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">4</span>
                    <div className="step-content">
                      <p>Enter your password</p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">5</span>
                    <div className="step-content">
                      <p>Click <strong>Sign In</strong></p>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Forgot Password Section */}
            <Card 
              className="docs-section" 
              id="forgot-password"
              ref={(el) => (sectionRefs.current['forgot-password'] = el)}
            >
              <Card.Body>
                <h2 className="section-title">3. Forgot Password</h2>
                
                <div className="step-group">
                  <div className="step-item">
                    <span className="step-number">1</span>
                    <div className="step-content">
                      <p>Go to <strong>dosspwd.com</strong></p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">2</span>
                    <div className="step-content">
                      <p>Click <strong>Login</strong> on the navigation bar</p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">3</span>
                    <div className="step-content">
                      <p>Click <strong>Forgot Password</strong></p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">4</span>
                    <div className="step-content">
                      <p>Enter your registered email address</p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">5</span>
                    <div className="step-content">
                      <p>Check your email inbox</p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">6</span>
                    <div className="step-content">
                      <p>Click the reset link or confirm button in the email</p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">7</span>
                    <div className="step-content">
                      <p>Enter your new password</p>
                      <Alert variant="warning" className="password-requirements">
                        <strong>Password Requirements:</strong>
                        <ul className="mb-0 mt-2">
                          <li>At least 8 characters</li>
                          <li>At least 3 of the following:
                            <ul>
                              <li>Lower case letters (a-z)</li>
                              <li>Upper case letters (A-Z)</li>
                              <li>Numbers (0-9)</li>
                              <li>Special characters (e.g. !@#$%^&*)</li>
                            </ul>
                          </li>
                        </ul>
                      </Alert>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">8</span>
                    <div className="step-content">
                      <p>Confirm your new password</p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">9</span>
                    <div className="step-content">
                      <p>You can now follow the usual login process</p>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Register New Person Section */}
            <Card 
              className="docs-section" 
              id="register"
              ref={(el) => (sectionRefs.current['register'] = el)}
            >
              <Card.Body>
                <h2 className="section-title">4. Register New Person with Disability</h2>
                
                <Alert variant="info" className="mb-3">
                  <strong><i className="bi bi-exclamation-triangle-fill me-1"></i>Note:</strong> You must be approved by an administrator to register new persons.
                </Alert>

                <div className="step-group">
                  <div className="step-item">
                    <span className="step-number">1</span>
                    <div className="step-content">
                      <p>Click <strong>Register</strong> on the menu</p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">2</span>
                    <div className="step-content">
                      <p>Select <strong>Province</strong></p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">3</span>
                    <div className="step-content">
                      <p>Select <strong>District</strong></p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">4</span>
                    <div className="step-content">
                      <p>Select <strong>DS Division</strong></p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">5</span>
                    <div className="step-content">
                      <p>Enter the person's <strong>Name with Initials</strong></p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">6</span>
                    <div className="step-content">
                      <p>Enter the <strong>NIC Number</strong> (if available)</p>
                      <Alert variant="info" className="mt-2 mb-0">
                        <strong><i className="bi bi-info-circle-fill me-1"></i>Note:</strong> NIC is not compulsory for persons without NIC, such as children or elderly persons without identification.
                      </Alert>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">7</span>
                    <div className="step-content">
                      <p><strong>Note the DoSS ID</strong> from the success screen and provide it to the person with disability for future use</p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">8</span>
                    <div className="step-content">
                      <p>To update additional details, click the <strong>Update Details</strong> button</p>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Update Existing Person Section */}
            <Card 
              className="docs-section" 
              id="update"
              ref={(el) => (sectionRefs.current['update'] = el)}
            >
              <Card.Body>
                <h2 className="section-title">5. Update Existing Person Data</h2>
                
                <div className="step-group">
                  <div className="step-item">
                    <span className="step-number">1</span>
                    <div className="step-content">
                      <p>Click <strong>Search</strong> on the menu</p>
                    </div>
                  </div>

                  <div className="subsection">
                    <h4>Option A: Using DoSS ID (Recommended)</h4>
                    <div className="step-group">
                      <div className="step-item">
                        <span className="step-number">2a</span>
                        <div className="step-content">
                          <p>Choose <strong>Unique ID</strong> (selected by default)</p>
                        </div>
                      </div>

                      <div className="step-item">
                        <span className="step-number">3a</span>
                        <div className="step-content">
                          <p>Enter the <strong>DoSS ID</strong></p>
                          <Alert variant="success" className="mt-2 mb-0">
                            <strong><i className="bi bi-check-circle-fill me-1"></i>Automatic Detection:</strong> The system will automatically detect Province, District, and DS Division from the ID.
                          </Alert>
                        </div>
                      </div>

                      <div className="step-item">
                        <span className="step-number">4a</span>
                        <div className="step-content">
                          <p>Click <strong>Search</strong></p>
                        </div>
                      </div>

                      <div className="step-item">
                        <span className="step-number">5a</span>
                        <div className="step-content">
                          <p>Once you find the result, click <strong>Update Details</strong></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="subsection">
                    <h4>Option B: Using NIC</h4>
                    <div className="step-group">
                      <div className="step-item">
                        <span className="step-number">2b</span>
                        <div className="step-content">
                          <p>Choose <strong>NIC Number</strong></p>
                        </div>
                      </div>

                      <div className="step-item">
                        <span className="step-number">3b</span>
                        <div className="step-content">
                          <p>Select <strong>Province</strong>, <strong>District</strong>, and <strong>DS Division</strong> of the person</p>
                        </div>
                      </div>

                      <div className="step-item">
                        <span className="step-number">4b</span>
                        <div className="step-content">
                          <p>Enter the <strong>NIC Number</strong></p>
                        </div>
                      </div>

                      <div className="step-item">
                        <span className="step-number">5b</span>
                        <div className="step-content">
                          <p>Click <strong>Search</strong></p>
                        </div>
                      </div>

                      <div className="step-item">
                        <span className="step-number">6b</span>
                        <div className="step-content">
                          <p>Once you find the result, click <strong>Update Details</strong></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Alert variant="info" className="mt-3">
                    <strong><i className="bi bi-lightbulb-fill me-1"></i>Tip:</strong> You can update each category at any time. There's no need to complete all updates in one session.
                  </Alert>
                </div>
              </Card.Body>
            </Card>

            {/* Analytics Section */}
            <Card 
              className="docs-section" 
              id="analytics"
              ref={(el) => (sectionRefs.current['analytics'] = el)}
            >
              <Card.Body>
                <h2 className="section-title">6. View Analytics</h2>
                
                <div className="step-group">
                  <div className="step-item">
                    <span className="step-number">1</span>
                    <div className="step-content">
                      <p>Click <strong>Analytics</strong> on the menu</p>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">2</span>
                    <div className="step-content">
                      <p>Choose your <strong>analytics level</strong>:</p>
                      <ul>
                        <li><strong>All Island:</strong> View nationwide statistics</li>
                        <li><strong>Province:</strong> View province-level data</li>
                        <li><strong>District:</strong> View district-level data</li>
                        <li><strong>DS Division:</strong> View DS Division-level data</li>
                      </ul>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">3</span>
                    <div className="step-content">
                      <p>For any level other than <strong>All Island</strong>, select the required locations:</p>
                      <ul>
                        <li>Province level: Select Province</li>
                        <li>District level: Select Province and District</li>
                        <li>DS Division level: Select Province, District, and DS Division</li>
                      </ul>
                    </div>
                  </div>

                  <div className="step-item">
                    <span className="step-number">4</span>
                    <div className="step-content">
                      <p>View the analytics dashboard with charts and tables</p>
                    </div>
                  </div>

                  <Alert variant="primary" className="mt-3">
                    <strong><i className="bi bi-bar-chart-fill me-1"></i>Advanced Analytics:</strong> For detailed filters and in-depth analysis, please contact Department of Social Services officers.
                  </Alert>
                </div>
              </Card.Body>
            </Card>

            {/* Support Section */}
            <Card 
              className="docs-section support-section" 
              id="support"
              ref={(el) => (sectionRefs.current['support'] = el)}
            >
              <Card.Body>
                <h2 className="section-title"><i className="bi bi-telephone-fill me-2"></i>Need Help?</h2>
                <p>For additional support or questions, please contact:</p>
                <div className="contact-info">
                  <p>
                    <strong>Department of Social Services</strong><br />
                    Website: <a href="https://www.socialservices.gov.lk/" target="_blank" rel="noreferrer">www.socialservices.gov.lk</a>
                  </p>
                </div>
              </Card.Body>
            </Card>
          </main>
        </div>
      </Container>
    </div>
  );
}

export default Documentation;